import {
   AppMessagesContext,
   Localization,
   SessionContext,
   actionSetErrorMessage,
} from "@viamap/viamap2-common";
import { Fragment, useContext, useState } from "react";
import { Spinner } from "react-bootstrap";
import { GlassButtonDropdown } from "src/components/GlassButtons";
import { GlassFoldUdBox, KeyValueLine, ViewButtonIconAsync } from "./PropertyComponents";
import { DC } from "src/dataCollector/DataCollector";
import { ExportData } from "src/managers/ExportData";
import { message } from "src/components/Message";
import { getTingbogDownloadErrorMessage } from "src/managers/TingbogDownload";
import { getServitutBilagDownloadFileName } from "src/managers/ServitutDownloadNaming";
import { WFStoGeoJson } from "src/functions/WFSutils";
import { Persistence } from "src/managers/Persistence";
import { MapitStateContext, actionAddDataLayer } from "src/states/MapitState";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { openWindow, WindowId } from "src/WindowManager/WindowState";
import { AnnotationContextType } from "src/modules/ANNModule/ANNTypes";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";

export type BilagAnnViewerContext = {
   servitutDocumentId: string;
   datoloebenr?: string;
   mainDocumentFileName?: string;
};

function isPdfBlob(blob: Blob, fileName?: string) {
   const t = (blob.type || "").toLowerCase();
   if (t.includes("pdf")) {
      return true;
   }
   return (fileName || "").toLowerCase().endsWith(".pdf");
}

function BilagAppendixActions(props: {
   bilagRef: string;
   bilagNumber: number;
   showImportGmlAction: boolean;
   showDownloadAction: boolean;
   downloadTitle: string;
   importGmlBilagToMap: (ref: string, fallbackFileName: string) => Promise<void>;
   bilagAnnViewerContext?: BilagAnnViewerContext;
}) {
   const {
      bilagRef,
      bilagNumber,
      showImportGmlAction,
      showDownloadAction,
      downloadTitle,
      importGmlBilagToMap,
      bilagAnnViewerContext,
   } = props;
   const { dispatch: windowDispatch } = useWindowContext();
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const { state: sessionState } = useContext(SessionContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const [loadingKey, setLoadingKey] = useState<string | null>(null);
   const isWorking = loadingKey !== null;

   const canOpenInAnnViewer =
      Boolean(bilagAnnViewerContext?.servitutDocumentId) &&
      hasAccessToFeature(Feature.ServitutEditor);

   async function fetchBilagBlob() {
      const { data, name } = (await DC.TL.fetchServitutBilag(bilagRef)) || {};
      if (!data) {
         throw new Error("No appendix data received");
      }
      return { data, name };
   }

   async function onDownloadToDisk() {
      const { data, name } = await fetchBilagBlob();
      const downloadName = getServitutBilagDownloadFileName(
         bilagAnnViewerContext?.mainDocumentFileName,
         bilagNumber,
         name || `bilag_${bilagRef}.pdf`,
      );
      ExportData.downloadBlob(downloadName, data);
   }

   async function onOpenInAnnPdfViewer() {
      const { data, name } = await fetchBilagBlob();
      if (!isPdfBlob(data, name)) {
         appMessageDispatch(
            actionSetErrorMessage(Localization.getText("No PDF available for this document")),
         );
         return;
      }
      const pdfUrl = URL.createObjectURL(data);
      windowDispatch(
         openWindow(WindowId.ANNPDFAnalyzerViewer, {
            pdfUrl,
            pdfBlob: data,
            aktId: `bilag_${bilagRef}`,
            documentId: bilagAnnViewerContext!.servitutDocumentId,
            contextType: AnnotationContextType.Servitut,
            organizationId: sessionState?.customerRef,
            userId: sessionState?.userRef,
            userName: sessionState?.userName,
            datoloebenr: bilagAnnViewerContext!.datoloebenr,
            documentFilename: getServitutBilagDownloadFileName(
               bilagAnnViewerContext?.mainDocumentFileName,
               bilagNumber,
               name || `bilag_${bilagRef}.pdf`,
            ),
         }),
      );
   }

   async function runWithLoading(key: string, fn: () => Promise<void>) {
      if (isWorking) {
         return;
      }
      setLoadingKey(key);
      try {
         await fn();
      } catch (error) {
         console.error("Bilag action failed", bilagRef, error);
         message.error(getTingbogDownloadErrorMessage());
      } finally {
         setLoadingKey(null);
      }
   }

   if (showImportGmlAction) {
      const gmlActions = [
         {
            key: "download",
            label: Localization.getText("Download file"),
            run: () => runWithLoading("download", onDownloadToDisk),
         },
         {
            key: "import",
            label: Localization.getText("ImageImport:MapOverlayButton"),
            run: async () => {
               if (isWorking) {
                  return;
               }
               setLoadingKey("import");
               try {
                  await importGmlBilagToMap(bilagRef, `bilag_${bilagRef}.gml`);
               } finally {
                  setLoadingKey(null);
               }
            },
         },
      ];
      return (
         <div className="tingbog-download-actions">
            <GlassButtonDropdown
               className={`tingbog-download-button tingbog-download-button-dropdown${isWorking ? " tingbog-download-button-dropdown--loading" : ""}`}
               title={
                  isWorking
                     ? Localization.getText("Loading...")
                     : Localization.getText("PDF actions")
               }
               placement="bottom-end"
               onClick={(e) => {
                  if (isWorking) {
                     e.preventDefault();
                  }
               }}
               options={gmlActions.map((a) => ({ text: a.label, value: a.key }))}
               onSelect={async (value) => {
                  const action = gmlActions.find((a) => a.key === value);
                  if (!action) {
                     return;
                  }
                  await action.run();
               }}
            >
               {isWorking ? (
                  <Spinner animation="border" size="sm" className="tingbog-download-spinner" />
               ) : (
                  <MIBIcon icon="pdf" />
               )}
            </GlassButtonDropdown>
         </div>
      );
   }

   if (showDownloadAction && canOpenInAnnViewer) {
      const pdfActions = [
         {
            key: "download",
            label: Localization.getText("Download PDF"),
            run: () => runWithLoading("download", onDownloadToDisk),
         },
         {
            key: "viewer",
            label: Localization.getText("Open PDF in editor"),
            run: () => runWithLoading("viewer", onOpenInAnnPdfViewer),
         },
      ];
      return (
         <div className="tingbog-download-actions">
            <GlassButtonDropdown
               className={`tingbog-download-button tingbog-download-button-dropdown${isWorking ? " tingbog-download-button-dropdown--loading" : ""}`}
               title={
                  isWorking
                     ? Localization.getText("Loading...")
                     : Localization.getText("PDF actions")
               }
               placement="bottom-end"
               onClick={(e) => {
                  if (isWorking) {
                     e.preventDefault();
                  }
               }}
               options={pdfActions.map((a) => ({ text: a.label, value: a.key }))}
               onSelect={async (value) => {
                  const action = pdfActions.find((a) => a.key === value);
                  if (!action) {
                     return;
                  }
                  await action.run();
               }}
            >
               {isWorking ? (
                  <Spinner animation="border" size="sm" className="tingbog-download-spinner" />
               ) : (
                  <MIBIcon icon="pdf" />
               )}
            </GlassButtonDropdown>
         </div>
      );
   }

   if (showDownloadAction) {
      return (
         <ViewButtonIconAsync
            title={downloadTitle}
            onClick={async () => {
               await runWithLoading("download", onDownloadToDisk);
            }}
         >
            <MIBIcon icon="pdf" />
         </ViewButtonIconAsync>
      );
   }

   return null;
}

export function BilagsVisning(props: {
   servitutXDOC: Document | null;
   bilagAnnViewerContext?: BilagAnnViewerContext;
   includeRefs?: string[];
   excludeRefs?: string[];
   foldetUd?: boolean;
}) {
   const { servitutXDOC, bilagAnnViewerContext, includeRefs, excludeRefs, foldetUd = true } = props;
   const { dispatch: mapitStateDispatch } = useContext(MapitStateContext);

   const isLikelyGmlBilag = (kode: string, beskrivelse: string, translatedKode: string | null): boolean => {
      if (kode === "38") {
         return true;
      }
      const fullText = `${beskrivelse || ""} ${translatedKode || ""}`.toLowerCase();
      return fullText.includes("gml");
   };

   const importGmlBilagToMap = async (ref: string, fallbackFileName: string) => {
      if (!ref) return;
      message.progress(Localization.getFormattedText("Loading {type} file...", { type: "GML" }));
      try {
         let { data, name } = await DC.TL.fetchServitutBilag(ref) || {};
         if (!data) {
            throw new Error("No appendix data received");
         }

         const gmlText = await data.text();
         const geoJsonLayers = WFStoGeoJson(gmlText).filter(({ geoJson }) => geoJson?.features && geoJson.features.length > 0);

         if (geoJsonLayers.length === 0) {
            message.error(Localization.getText("No features found in GML file."));
            return;
         }

         geoJsonLayers.forEach(({ layerName, geoJson }) => {
            Persistence.createLayersFromGeoJSON(layerName || "GML", geoJson, new Date(), (li) => {
               mapitStateDispatch(actionAddDataLayer(li, true));
            });
         });

         message.info(
            Localization.getFormattedText("Loaded {count} layers from {file}", {
               count: geoJsonLayers.length,
               file: name || fallbackFileName
            })
         );
      } catch (error) {
         console.error("Failed to import GML appendix", ref, error);
         message.error(error instanceof Error ? error.message : "GML import failed");
      } finally {
         message.progress();
      }
   };

   const includeSet = includeRefs ? new Set(includeRefs.map((ref) => ref.toLowerCase())) : null;
   const excludeSet = new Set((excludeRefs || []).map((ref) => ref.toLowerCase()));
   const samling = servitutXDOC instanceof Document
      ? servitutXDOC.getElementsByTagName("BilagReferenceSamling")[0]
      : null;
   const rows = samling
      ? Array.from(samling.children).filter((r: Element) => {
         const ref = (r.getElementsByTagName("BilagReference")[0]?.textContent || "").trim().toLowerCase();
         if (!ref) {
            return false;
         }
         if (excludeSet.has(ref)) {
            return false;
         }
         if (includeSet && !includeSet.has(ref)) {
            return false;
         }
         return true;
      })
      : [];
   const seenRefs = new Set(
      rows.map((r) => (r.getElementsByTagName("BilagReference")[0]?.textContent || "").trim().toLowerCase()).filter(Boolean),
   );
   const extraRefs = (includeRefs || []).filter((ref) => {
      const normalized = ref.trim().toLowerCase();
      return Boolean(normalized) && !excludeSet.has(normalized) && !seenRefs.has(normalized);
   });
   if (!rows.length && !extraRefs.length) {
      return null;
   }

   return (
      <GlassFoldUdBox title={Localization.getText("AppendixReference")} foldetUd={foldetUd}>
         {rows.map((r: Element, idx: number) => {
            let ref = r.getElementsByTagName("BilagReference")[0]?.textContent || "";
            let kode = r.getElementsByTagName("BilagReferenceKode")[0]?.textContent || "";
            let beskrivelse = r.getElementsByTagName("Afsnit")[0]?.textContent || "";

            const linkUrl = ref ? `https://www.tinglysning.dk/tinglysning/rest/bilag/${ref}` : null;
            const lang = typeof Localization.getLanguage === 'function' ? Localization.getLanguage() : 'da';
            const translatedKode = kode ? (translationTableBilagsReferenceKode?.[lang]?.[kode] ?? translationTableBilagsReferenceKode?.['da']?.[kode] ?? kode) : null;
            const showDownloadAction = Boolean(ref);
            const showImportGmlAction = showDownloadAction && isLikelyGmlBilag(kode, beskrivelse, translatedKode);
            const downloadTitle = showImportGmlAction ? Localization.getText("Download file") : Localization.getText("Download PDF");

            return (
               <Fragment key={ref ? `bilag-${ref}` : `bilag-idx-${idx}`}>
                  <KeyValueLine valKey={
                     linkUrl ? (
                        <a href={linkUrl} target="_blank" rel="noopener noreferrer" >
                           Bilag {idx + 1}
                        </a>
                     ) : (
                        beskrivelse || Localization.getText("No data")
                     )
                  } value={<>
                     <div
                        style={{
                           display: "grid",
                           gridTemplateColumns: "minmax(0, 1fr) auto",
                           alignItems: "start",
                           columnGap: "0.5rem",
                           width: "100%",
                           textAlign: "left",
                           padding: 0
                        }}
                     >
                        <div style={{ minWidth: 0, padding: 0, overflowWrap: "anywhere", whiteSpace: "normal" }}>
                           {beskrivelse ? <div style={{ padding: 0 }}>{beskrivelse}</div> : null}
                           {translatedKode ? <div style={{ padding: 0 }}>{translatedKode}</div> : null}
                        </div>
                        {(showDownloadAction || showImportGmlAction) ? (
                           <div
                              style={{
                                 display: "flex",
                                 alignItems: "center",
                                 justifyContent: "flex-end",
                                 gap: "0.35rem",
                                 flexWrap: "wrap",
                                 padding: 0
                              }}
                           >
                              <BilagAppendixActions
                                 bilagRef={ref}
                                 bilagNumber={idx + 1}
                                 showImportGmlAction={showImportGmlAction}
                                 showDownloadAction={showDownloadAction}
                                 downloadTitle={downloadTitle}
                                 importGmlBilagToMap={importGmlBilagToMap}
                                 bilagAnnViewerContext={bilagAnnViewerContext}
                              />
                           </div>
                        ) : null}
                     </div>
                  </>} infoText={ref} />
               </Fragment>
            );
         })}
         {extraRefs.map((ref, idx) => {
            const bilagNumber = rows.length + idx + 1;
            const linkUrl = `https://www.tinglysning.dk/tinglysning/rest/bilag/${ref}`;
            return (
               <Fragment key={`bilag-${ref}`}>
                  <KeyValueLine valKey={
                     <a href={linkUrl} target="_blank" rel="noopener noreferrer" >
                        Bilag {bilagNumber}
                     </a>
                  } value={<>
                     <div
                        style={{
                           display: "grid",
                           gridTemplateColumns: "minmax(0, 1fr) auto",
                           alignItems: "start",
                           columnGap: "0.5rem",
                           width: "100%",
                           textAlign: "left",
                           padding: 0
                        }}
                     >
                        <div style={{ minWidth: 0, padding: 0 }} />
                        <div
                           style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: "0.35rem",
                              flexWrap: "wrap",
                              padding: 0
                           }}
                        >
                           <BilagAppendixActions
                              bilagRef={ref}
                              bilagNumber={bilagNumber}
                              showImportGmlAction={false}
                              showDownloadAction={true}
                              downloadTitle={Localization.getText("Download PDF")}
                              importGmlBilagToMap={importGmlBilagToMap}
                              bilagAnnViewerContext={bilagAnnViewerContext}
                           />
                        </div>
                     </div>
                  </>} infoText={ref} />
               </Fragment>
            );
         })}
      </GlassFoldUdBox>
   );
}


// Translation table for BilagReferenceKode (updated)
const translationTableBilagsReferenceKode: any = {
   da: {
      "1": "Udlægsattest, udstedt før 25. marts 2008",
      "2": "Arrestattest, udstedt før 25. marts 2008",
      "3": "Ejendomsdom, med ankepåtegning, afsagt før 25. marts 2008",
      "4": "Modifikationsdom, med ankepåtegning, afsagt før 25. marts 2008",
      "5": "Auktionsskøde med evt. transportskøde",
      "6": "Bobestyrerattest, udstedt før 25. marts 2008",
      "7": "Kuratorattest, udstedt før 25. marts 2008",
      "8": "Tilladelse til erhvervelse af fast ejendom i Danmark, udstedt af JM",
      "9": "Tilladelse til erhvervelse, udstedt af Statsamt",
      "10": "Tilladelse fra Skov- og Naturstyrelsen om selskabs erhvervelse af fast ejendom",
      "11": "Dokumentation fra menighedsråd om tegningsreglerne for rådet",
      "12": "Attestation fra Direktoratet for Fødevareerhverv om behørig afgivelse af erklæringer i medfør af landbrugslovens erhvervsbestemmelser",
      "13": "Godkendelse fra Kirkeministeriet eller Stiftsøvrigheden til salg eller pantsætning af jordelig kirkelig gods",
      "14": "Tilladelse til erhvervelse af arbejderbolig",
      "15": "Stiftelsesdokumenter for selskab under stiftelse",
      "16": "Anden dokumentation for, at anmeldelse af nystiftet selskab er sket overfor Erhvervs- og Selskabsstyrelsen indenfor de i lovens krævede frister",
      "17": "Kommunalbestyrelsens samtykke til salg af ejendom fra Almen boligorganisation",
      "18": "Matrikelkort med attestationen \"e-rids\" (ved salg af nyudstykket parcel)",
      "19": "Dokumentation for generalforsamling om tegningsregler m.v. for forening",
      "20": "Vedtægter m.v. for forening",
      "21": "Interessentskabskontrakt med tegningsregler m.v.",
      "22": "Dispensation eller tilladelse fra Jordbrugskommissionen vedr. erhvervelse af landbrugsejendom",
      "23": "Skiftefuldmagter, udstedt før 25. marts 2008",
      "24": "Notering af umyndiggørelse på fast ejendom",
      "25": "Notering af ægtepagt som særeje",
      "26": "Tinglysning af ægtepagt som adkomst",
      "27": "Meddelelse om konkurs, udstedt før 25. marts 2008",
      "28": "Samme vedr. gældssanering, tvangsakkord",
      "29": "Tinglysning af vielsesattest rådighedsindskrænkende",
      "30": "Prokura, gældende før 25. marts 2008",
      "31": "Samtykke fra indehaver af forkøbsret eller tilbagekøbsret",
      "32": "Original bebestyrerattest, hvis en ejendom sælges af bobestyrer",
      "33": "Original skifteretsattest, hvis ejendom sælges af et dødsbo",
      "34": "Godkendelse fra kommunalbestyrelsen til tinglysning af servitut, jf. planlovens § 42",
      "35": "Samtykke fra bortforpagtning af landbrugsejendom fra Direktoratet for Fødevareerhverv",
      "36": "Godkendelse af pantsætning fra værge",
      "37": "Godkendelse af pantsætning fra Almennyttige boligselskaber",
      "38": "GML-dokument med koordinater til servitutter og bygning på lejet grund",
      "80": "Andet",
      "90": "Intern brug"
   },
   en: {
      "1": "Certificate of Execution issued prior to 25 March 2008",
      "2": "Certificate of Attachment issued prior to 25 March 2008",
      "3": "Judgment concerning real property, with endorsement of appeal, rendered prior to 25 March 2008",
      "4": "Modification judgment, with endorsement of appeal, rendered prior to 25 March 2008",
      "5": "Deed of Auction, including any Deed of Assignment",
      "6": "Certificate of Estate Administration issued prior to 25 March 2008",
      "7": "Trustee’s Certificate issued prior to 25 March 2008",
      "8": "Permit for acquisition of real property in Denmark issued by the Ministry of Justice",
      "9": "Permit for acquisition issued by the County Authority (Statsamt)",
      "10": "Authorization from the Danish Forest and Nature Agency regarding corporate acquisition of real property",
      "11": "Documentation from the Parish Council concerning the Council’s signing authority",
      "12": "Attestation from the Directorate for Food Business confirming proper submission of declarations pursuant to the agricultural law’s business provisions",
      "13": "Approval from the Ministry of Ecclesiastical Affairs or the Diocesan Authority for sale or mortgaging of church-owned land",
      "14": "Permit for acquisition of workers’ housing",
      "15": "Founding documents for a company under incorporation",
      "16": "Other documentation evidencing that notification of a newly incorporated company has been duly submitted to the Danish Commerce and Companies Agency within the statutory deadlines",
      "17": "Consent from the Municipal Council for sale of property by a Public Housing Organization",
      "18": "Cadastral map with the endorsement 'e-plan' (for sale of newly subdivided parcel)",
      "19": "Documentation from the general assembly concerning the association’s signing authority, etc.",
      "20": "Articles of Association and related documents for an association",
      "21": "Partnership Agreement including signing authority, etc.",
      "22": "Exemption or permit from the Agricultural Commission concerning acquisition of agricultural property",
      "23": "Probate Power of Attorney issued prior to 25 March 2008",
      "24": "Registration of legal incapacitation relating to real property",
      "25": "Registration of prenuptial agreement establishing separate property",
      "26": "Registration of prenuptial agreement as title",
      "27": "Notice of bankruptcy issued prior to 25 March 2008",
      "28": "Same concerning debt restructuring or compulsory composition",
      "29": "Registration of marriage certificate restricting right of disposal",
      "30": "Power of Procuration valid prior to 25 March 2008",
      "31": "Consent from holder of pre-emption or repurchase right",
      "32": "Original Certificate of Estate Administration where property is sold by an estate administrator",
      "33": "Original Certificate from the Probate Court where property is sold by an estate",
      "34": "Approval from the Municipal Council for registration of easement pursuant to section 42 of the Danish Planning Act",
      "35": "Consent from the Directorate for Food Business concerning lease of agricultural property",
      "36": "Approval of mortgage granted by legal guardian",
      "37": "Approval of mortgage granted by non-profit housing associations",
      "38": "GML document containing coordinates for easements and buildings on leased land",
      "80": "Other",
      "90": "Internal use"
   }
}
